import { useEffect, useMemo, useState } from 'react';
import { Segmented } from 'antd';
import AppBar from '../components/AppBar';
import PeriodPicker, { presetRange } from '../components/PeriodPicker';
import MemberPerformanceSheet from '../components/MemberPerformanceSheet';
import { usePaginatedList } from '../hooks/usePaginatedList';
import usePerformanceScopes from '../hooks/usePerformanceScopes';
import { Chip, EmptyState, InfiniteSentinel, SearchBar, Skeletons, RM0, RMk, initials } from '../components/kit';
import { useCountingRM } from '../hooks/useCountUp';
import { S } from '../lib/space';
import * as perfApi from '../api/performance';

/**
 * Team & Branch performance.
 *
 * One screen, one component set, three scopes — Direct (Gen 1) · All gens (1–4) ·
 * Branch (assigned Centre Directors only). Which segments exist is decided by the
 * server's /performance/scopes response, never by the client: the switcher renders
 * what the caller actually holds, and naming a scope they don't hold is a 403.
 *
 * Pipeline figures are deliberately NOT period-filtered — see the note by the funnel.
 */

const SORTS = [
  { key: 'gci', label: 'GCI' },
  { key: 'deals', label: 'Deals' },
  { key: 'volume', label: 'Volume' },
  { key: 'name', label: 'Name' },
];

// Funnel colours, in stage order — grey draft → warming blues → green at HQ.
const GROUP_COLOR = {
  DRAFT: 'color-mix(in srgb,var(--muted) 45%,transparent)',
  INVOICE: 'var(--accent)',
  CLAIM: 'var(--primary)',
  HQ: 'var(--ok)',
};

const RANK_LABEL = { AGENT: 'Agent', TL: 'Team Leader', CD: 'Centre Director' };

export default function TeamPerformancePage() {
  const { scopes, ready, can, counts, branches } = usePerformanceScopes();

  const [scope, setScope] = useState(null); // set once scopes land
  const [branchId, setBranchId] = useState(null); // null = all my branches
  const [period, setPeriod] = useState(() => presetRange('This year'));
  const [sort, setSort] = useState('gci');
  const [q, setQ] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [summary, setSummary] = useState(null);
  const [attention, setAttention] = useState([]);
  const [openMember, setOpenMember] = useState(null);

  // Land on the widest scope the caller holds: a Centre Director opens on their
  // branch, everyone else on their direct recruits.
  useEffect(() => {
    if (!ready || scope) return;
    setScope(can.branch ? 'branch' : 'direct');
    if (can.branch && branches.length === 1) setBranchId(branches[0].id);
  }, [ready, scope, can.branch, branches]);

  const segments = useMemo(() => [
    ...(can.direct ? [{ value: 'direct', label: 'Direct' }] : []),
    ...(can.allGens && counts.allGens > counts.direct ? [{ value: 'team', label: 'All gens' }] : []),
    ...(can.branch ? [{ value: 'branch', label: 'Branch' }] : []),
  ], [can, counts]);

  // Every scoped call takes the same params, so they are built once. Null until the
  // caller is known to hold a scope — firing summary/members for an agent with no
  // team would just earn a 403 behind an empty state they are already looking at.
  const scopeParams = useMemo(() => (scope && segments.length ? {
    scope,
    ...(scope === 'branch' && branchId ? { branch_id: branchId } : {}),
    ...(period?.from ? { from: period.from } : {}),
    ...(period?.to ? { to: period.to } : {}),
  } : null), [scope, segments.length, branchId, period]);

  useEffect(() => {
    if (!scopeParams) return undefined;
    let live = true;
    setSummary(null);
    perfApi.getPerformanceSummary(scopeParams)
      .then((r) => { if (live) setSummary(r?.data ?? r); })
      .catch(() => { if (live) setSummary(false); });
    perfApi.listPerformanceAttention(scopeParams)
      .then((r) => { if (live) setAttention((r?.data ?? r) || []); })
      .catch(() => { if (live) setAttention([]); });
    return () => { live = false; };
  }, [scopeParams]);

  const memberParams = useMemo(() => (scopeParams ? {
    ...scopeParams,
    sort,
    ...(q ? { q } : {}),
    ...(includeInactive ? { include_inactive: true } : {}),
  } : {}), [scopeParams, sort, q, includeInactive]);

  const members = usePaginatedList(perfApi.listPerformanceMembers, {
    limit: 20,
    params: memberParams,
    enabled: !!scopeParams,
  });

  // Bar width on each row is a share of the top producer, so the list reads as a
  // ranking at a glance. Only the loaded page is compared — the leader is on it,
  // because the default sort is GCI descending.
  const topGci = Math.max(1, ...members.items.map((m) => Number(m.gci) || 0));

  const branchName = branchId
    ? (branches.find((b) => b.id === branchId)?.name || 'Branch')
    : 'All my branches';

  // Head count comes from the summary once it lands, so it matches the list below.
  // The raw scope count includes inactive agents that the list hides by default, and
  // a header reading "26 direct recruits" over a list of 10 rows reads as a bug.
  const heads = summary?.members?.total
    ?? (scope === 'branch' ? counts.branch : scope === 'team' ? counts.allGens : counts.direct);
  const sub = !ready ? '' : scope === 'branch'
    ? `${branchName} · ${heads} agents`
    : `${heads} ${scope === 'team' ? 'in downline' : 'direct recruits'}`;

  // No downline and no branch: nothing to aggregate. The route still resolves —
  // the More menu hides its row, but a bookmark or a shared link has to land
  // somewhere that explains itself.
  if (ready && segments.length === 0) {
    return (
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <AppBar back title="Team performance" sub="no downline yet" />
        <div style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
          <EmptyState
            title="No recruits yet"
            sub="Recruit your first agent and their production shows up here — along with the override you earn from it."
          />
        </div>
      </div>
    );
  }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <AppBar back title="Team performance" sub={sub} />

      {!ready || !scope ? (
        <div style={{ padding: S.page }}><Skeletons n={4} height={92} /></div>
      ) : (
        <div className="scrx" style={{ flex: 1, overflowY: 'auto', padding: `0 ${S.page}px ${S.block}px`, display: 'flex', flexDirection: 'column', gap: S.block }}>

          {/* Scope bar — pinned. Switching Direct / All gens / Branch reframes the
              whole screen, and the member list is long enough that reaching the tabs
              meant scrolling all the way back up.
              Three details make it behave:
                · the scroll container carries NO top padding (it lives in this bar
                  instead), because sticky `top: 0` resolves to the container's CONTENT
                  edge — with padding up there, rows stayed visible in the gap above;
                · the negative side margins bleed the bar out to the gutters, so content
                  passing behind is covered edge to edge rather than in an inset strip
                  — they are derived from S.page for exactly that reason;
                · paddingBottom + an equal negative marginBottom paint 10px of cover
                  under the tabs without adding 10px to the resting layout.
              The period row is deliberately NOT in here — see below. */}
          {(segments.length > 1 || (scope === 'branch' && branches.length > 1)) && (
            <div
              style={{
                position: 'sticky',
                top: 0,
                zIndex: 3,
                background: 'var(--bg)',
                // Bleed must track the container's side padding exactly — see the
                // note in lib/space.js. Hard-coding 16 here while the gutter is
                // S.page leaves a 2px strip of content showing past each end.
                margin: `0 -${S.page}px -10px`,
                padding: `14px ${S.page}px 10px`,
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
              }}
            >
              {segments.length > 1 && (
                <Segmented
                  block
                  value={scope}
                  onChange={setScope}
                  options={segments}
                />
              )}

              {/* Branch picker — only when the CD directs more than one. A single-branch
                  CD gets a label, not a control with one option. */}
              {scope === 'branch' && branches.length > 1 && (
                <div className="scrx" style={{ display: 'flex', gap: 8, overflowX: 'auto' }}>
                  <Pill on={!branchId} onClick={() => setBranchId(null)} label="All my branches" />
                  {branches.map((b) => (
                    <Pill key={b.id} on={branchId === b.id} onClick={() => setBranchId(b.id)} label={b.name} />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Period scrolls away with the content rather than pinning alongside the
              tabs. Picking "Custom" grows this control by a 46px row of date fields,
              which would have taken the frozen header to ~215px — a quarter of the
              screen held for filters. The selected period is restated in the hero
              ("TEAM GCI · 2026"), so nothing is lost by letting it scroll. */}
          <PeriodPicker value={period} onChange={setPeriod} />

          {summary === false ? (
            <div style={{ fontSize: 13, color: 'var(--muted)', padding: '12px 2px' }}>
              Couldn&rsquo;t load these figures just now.
            </div>
          ) : !summary ? (
            <Skeletons n={2} height={96} />
          ) : (
            <>
              {/* Entrance beats. The scope bar and period picker above are
                  deliberately NOT animated: `rise-in` travels on `transform`,
                  and a transform on the sticky bar would make it its own
                  containing block and kill the stickiness outright. They are
                  controls anyway — only the figures they produce arrive. */}
              <div className="rise-in" style={{ background: 'linear-gradient(135deg,#241854,#18345E)', borderRadius: 20, padding: 20, color: '#fff', display: 'flex', flexDirection: 'column', gap: 11 }}>
                {scope === 'branch' && summary.branchYtd ? (
                  <BranchHero s={summary} branchName={branchName} />
                ) : (
                  <TeamHero s={summary} />
                )}
              </div>

              {/* KPI tiles */}
              <div className="rise-in d1" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: S.grid }}>
                <Tile label="DEALS CLOSED" value={String(summary.deals)} sub={summary.deals ? `avg ${RMk(summary.avgDeal)}` : null} />
                <Tile label="PIPELINE" value={RMk(summary.pipeline.gci)} sub={`${summary.pipeline.deals} open`} color="var(--primary)" />
                <Tile label="DORMANT" value={String(summary.members.dormant)} sub={`of ${summary.members.total}`} color={summary.members.dormant ? 'var(--warn)' : undefined} />
              </div>

              {/* pipeline funnel */}
              {summary.pipeline.groups.length > 0 && (
                <div className="rise-in d1" style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: S.radius, padding: S.cardPad }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10, marginBottom: 12 }}>
                    <span style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '.07em', color: 'var(--muted)' }}>PIPELINE BY STAGE</span>
                    <span style={{ fontSize: 11.5, color: 'var(--muted)' }}>open now, not {summary.period.label}</span>
                  </div>
                  {/* Segments grow out of the left edge together, so the bar
                      reads as one quantity being split rather than four bars.
                      `fillup` is the app's existing bar entrance (TierLadder). */}
                  <div style={{ display: 'flex', height: 10, borderRadius: 999, overflow: 'hidden', gap: 2, marginBottom: 13 }}>
                    {summary.pipeline.groups.map((g) => (
                      <span key={g.key} style={{ width: `${(g.gci / Math.max(1, summary.pipeline.gci)) * 100}%`, background: GROUP_COLOR[g.key] || 'var(--accent)', animation: 'fillup .8s ease backwards' }} />
                    ))}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
                    {summary.pipeline.groups.map((g) => (
                      <div key={g.key} style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 13 }}>
                        <span style={{ width: 9, height: 9, borderRadius: 3, background: GROUP_COLOR[g.key] || 'var(--accent)', flex: 'none' }} />
                        <span style={{ flex: 1, fontWeight: 500 }}>{g.label}</span>
                        <span style={{ color: 'var(--muted)' }}>{g.deals}</span>
                        <span style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', minWidth: 66, textAlign: 'right' }}>{RMk(g.gci)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Override the server could not trace to a member in view — the producing
              sale sits outside this scope or period. Shown only when it is a real
              slice of the total, so the member rows not adding up to the hero figure
              is explained rather than looking like a bug. */}
          {summary?.override > 0 && summary.overrideUnattributed / summary.override > 0.02 && (
            <div style={{ fontSize: 11.5, color: 'var(--muted)', lineHeight: 1.55, padding: '0 2px', marginTop: -8 }}>
              {RMk(summary.overrideUnattributed)} of your override came from deals outside this
              {scope === 'direct' ? ' direct-recruit' : ''} view, so it is in the total above but not against a member below.
            </div>
          )}

          {/* Needs attention — ONE grouped card with divided rows, the same shape the
              Home screen's recent-activity list and the Earnings payout ledger use.
              It replaced four separate cards each carrying a coloured left stripe:
              the stripe only repeated what the chip on the right already says, and
              four stacked cards with red and amber edges shouted louder than the
              exceptions they were describing. Severity lives in one place now — the
              chip — and the section reads as a queue rather than four alerts. */}
          {attention.length > 0 && (
            <div className="rise-in d2" style={{ display: 'flex', flexDirection: 'column', gap: S.row }}>
              <SectionLabel>NEEDS ATTENTION · {attention.length}</SectionLabel>
              <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: S.radius, overflow: 'hidden' }}>
                {attention.map((a, i) => (
                  <div key={i} style={{ display: 'flex', gap: 11, alignItems: 'center', padding: S.rowPad, borderBottom: i < attention.length - 1 ? '1px solid var(--border)' : 'none' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.3 }}>{a.text}</div>
                      {a.sub && <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 3 }}>{a.sub}</div>}
                    </div>
                    {a.badge && <Chip label={a.badge} tone={a.tone === 'bad' ? 'bad' : 'warn'} />}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Leaderboard. Its header, search and rows are one group with a tighter
              internal gap than the blocks above — at the full block rhythm the
              rows drifted apart until the list stopped reading as a list. */}
          <div className="rise-in d3" style={{ display: 'flex', flexDirection: 'column', gap: S.row }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
              <SectionLabel>{scope === 'branch' ? 'AGENTS' : 'MEMBERS'}{members.pagination ? ` · ${members.pagination.total}` : ''}</SectionLabel>
              <div className="scrx" style={{ display: 'flex', gap: 8, overflowX: 'auto' }}>
                {SORTS.map((s) => (
                  <Pill key={s.key} small on={sort === s.key} onClick={() => setSort(s.key)} label={s.label} />
                ))}
              </div>
            </div>

            {/* Search only where the roster is long enough to need it. */}
            {scope === 'branch' && <SearchBar onSearch={setQ} placeholder="Search agent or REN…" />}

            {members.loading ? (
              <Skeletons n={4} height={76} />
            ) : members.empty ? (
              <EmptyState
                title={q ? 'No agent matches' : `Nothing to show for ${summary?.period?.label || 'this period'}`}
                sub={q ? 'Try a different name or REN number.' : 'Members appear here once they are on the roster.'}
              />
            ) : (
              <>
                {members.items.map((m) => (
                  <div
                    key={m.agent_id}
                    className="tap"
                    onClick={() => setOpenMember(m)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpenMember(m); } }}
                    style={{ display: 'flex', alignItems: 'center', gap: 14, background: 'var(--surface)', border: `1px solid ${m.is_me ? 'var(--primary)' : 'var(--border)'}`, borderRadius: S.radius, padding: '15px 16px', cursor: 'pointer' }}
                  >
                    <span style={{ width: 18, textAlign: 'center', fontSize: 11.5, fontWeight: 700, color: 'var(--muted)', flex: 'none' }}>{m.rank}</span>
                    <span style={{ width: 38, height: 38, borderRadius: '50%', background: m.is_me ? 'var(--primary)' : 'var(--chip)', color: m.is_me ? '#fff' : 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12.5, fontWeight: 700, flex: 'none' }}>
                      {initials(m.name)}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                        <span style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.name}</span>
                        {m.is_me && <Chip label="You" tone="brand" style={{ flex: 'none' }} />}
                        {m.dormant && <Chip label="Dormant" tone="warn" style={{ flex: 'none' }} />}
                        {m.is_new && !m.dormant && <Chip label="New" tone="brand" style={{ flex: 'none' }} />}
                        {!m.is_active && <Chip label="Inactive" tone="muted" style={{ flex: 'none' }} />}
                      </div>
                      <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {rowSub(m)}
                      </div>
                      <div style={{ height: 4, borderRadius: 999, background: 'var(--chip)', marginTop: 8, overflow: 'hidden' }}>
                        <span style={{ display: 'block', height: '100%', width: `${Math.max(0, (Number(m.gci) || 0) / topGci * 100)}%`, background: 'var(--accent)', borderRadius: 999, animation: 'fillup .9s ease backwards' }} />
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', flex: 'none' }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: m.gci > 0 ? 'var(--ok)' : 'var(--muted)', fontVariantNumeric: 'tabular-nums' }}>{RMk(m.gci)}</div>
                      <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 2 }}>{m.deals} deal{m.deals === 1 ? '' : 's'}</div>
                    </div>
                  </div>
                ))}
                <InfiniteSentinel hasMore={members.hasMore} loading={members.loadingMore} onReach={members.loadMore} />
              </>
            )}
          </div>

          {/* Inactive agents keep contributing to the totals above — the toggle only
              decides whether they are listed, so the rows still reconcile. */}
          {summary?.members?.inactive > 0 && (
            <div
              onClick={() => setIncludeInactive((v) => !v)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setIncludeInactive((v) => !v); } }}
              style={{ textAlign: 'center', fontSize: 13, color: 'var(--primary)', fontWeight: 600, padding: '6px 0 2px', cursor: 'pointer' }}
            >
              {includeInactive
                ? 'Hide inactive agents'
                : `Include ${summary.members.inactive} inactive agent${summary.members.inactive === 1 ? '' : 's'}`}
            </div>
          )}

          <div style={{ height: 14 }} />
        </div>
      )}

      <MemberPerformanceSheet
        open={!!openMember}
        member={openMember}
        period={period}
        onClose={() => setOpenMember(null)}
      />
    </div>
  );
}

// ── hero variants ──────────────────────────────────────────────────────────

const TeamHero = ({ s }) => {
  const gci = useCountingRM(s.gci);
  return (
  <>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
      <span style={{ fontSize: 13, fontWeight: 600 }}>TEAM GCI · {s.period.label}</span>
      <span style={{ fontSize: 11.5, opacity: 0.74 }}>{s.members.producing} of {s.members.total} producing</span>
    </div>
    <div style={{ fontSize: 29, fontWeight: 700, letterSpacing: '-.02em', lineHeight: 1.1, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{gci}</div>
    <div style={{ fontSize: 12.5, opacity: 0.82 }}>{s.deals} deal{s.deals === 1 ? '' : 's'} closed · {RMk(s.volume)} sales volume</div>
    <div style={{ display: 'flex', gap: 14, borderTop: '1px solid rgba(255,255,255,.16)', paddingTop: 12, marginTop: 2 }}>
      {s.override != null && <HeroStat k="OVERRIDE TO YOU" v={RMk(s.override)} />}
      <HeroStat k="PIPELINE" v={RMk(s.pipeline.gci)} />
      <HeroStat k="AVG / MEMBER" v={RMk(s.avgPerMember)} />
    </div>
  </>
  );
};

/**
 * Branch hero leads on the HQ royalty band, because that is the number a Centre
 * Director is actually accountable for. It is a different GCI basis from the team
 * hero — branch_performance_log accrues at RECEIPT, while team GCI is booked at
 * sale — so it is labelled YTD and carries the basis note underneath. The two will
 * not tie out, and pretending otherwise is how the screen loses trust.
 */
const BranchHero = ({ s, branchName }) => {
  const b = s.branchYtd;
  // Both variants lead on a figure, but a different one — booked GCI when there
  // is no royalty position to show, YTD when there is. Hooks cannot sit behind
  // the branch below, so both are counted and the variant picks one.
  const bookedGci = useCountingRM(s.gci);
  const ytdGci = useCountingRM(b.ytdGci);
  const pct = b.bandCeil ? Math.min(100, Math.max(0, ((b.ytdGci - b.bandFloor) / (b.bandCeil - b.bandFloor)) * 100)) : 100;

  // The YTD hero exists to carry the HQ royalty band, and the band only means
  // something for ONE branch that has a log row. Two cases fall back to booked GCI:
  //
  //  · no log row — branch_performance_log is written on the first payout RELEASE,
  //    so a branch with closed deals but nothing collected yet has none, and a
  //    confident "RM 0 YTD" above a full deal list reads as a broken screen;
  //  · "all my branches" — each branch sits in its own band, so there is no combined
  //    rate, and a combined YTD is the one figure on the card nobody can act on.
  //
  // Either way the YTD number stays visible as a secondary stat rather than leading.
  if (!b.hasLog || b.royaltyRate == null) {
    return (
      <>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
          <span style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>GCI BOOKED · {s.period.label}</span>
          <span style={{ fontSize: 11.5, opacity: 0.74, flex: 'none' }}>{s.members.producing} of {s.members.total} producing</span>
        </div>
        <div style={{ fontSize: 29, fontWeight: 700, letterSpacing: '-.02em', lineHeight: 1.1, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{bookedGci}</div>
        <div style={{ fontSize: 12.5, opacity: 0.84 }}>
          {branchName} · {s.deals} deal{s.deals === 1 ? '' : 's'} closed · {RMk(s.volume)} volume
        </div>
        <div style={{ display: 'flex', gap: 14, borderTop: '1px solid rgba(255,255,255,.16)', paddingTop: 12, marginTop: 2 }}>
          <HeroStat k="RELEASED YTD" v={RMk(b.ytdGci)} />
          <HeroStat k="PIPELINE" v={RMk(s.pipeline.gci)} />
          <HeroStat k="AVG DEAL" v={RMk(s.avgDeal)} />
        </div>
        <div style={{ fontSize: 11.5, opacity: 0.62, lineHeight: 1.5 }}>
          {b.hasLog
            ? 'The HQ royalty band is set per branch — pick one above to see its position.'
            : `No HQ royalty position recorded for ${b.year} yet; that figure accrues when payment is received.`}
        </div>
      </>
    );
  }

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
        <span style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>BRANCH GCI · {b.year} YTD</span>
        <span style={{ fontSize: 11.5, opacity: 0.74, flex: 'none' }}>{s.members.producing} of {s.members.total} producing</span>
      </div>
      <div style={{ fontSize: 29, fontWeight: 700, letterSpacing: '-.02em', lineHeight: 1.1, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{ytdGci}</div>
      {b.royaltyRate != null ? (
        <>
          <div style={{ height: 7, borderRadius: 999, background: 'rgba(255,255,255,.18)', overflow: 'hidden' }}>
            <span style={{ display: 'block', width: `${pct}%`, height: '100%', background: '#5AA1EA', borderRadius: 999, animation: 'fillup .9s ease backwards' }} />
          </div>
          <div style={{ fontSize: 12.5, opacity: 0.84 }}>
            HQ royalty band <b>{b.royaltyRate}%</b>
            {b.nextRate ? ` · ${RMk(b.toNextRate)} more to reach ${b.nextRate}%` : ' · top band'}
          </div>
        </>
      ) : (
        <div style={{ fontSize: 12.5, opacity: 0.84 }}>
          {branchName} · the royalty band is set per branch, so it is not shown on a combined total
        </div>
      )}
      <div style={{ display: 'flex', gap: 14, borderTop: '1px solid rgba(255,255,255,.16)', paddingTop: 12, marginTop: 2 }}>
        <HeroStat k={`DEALS · ${s.period.label}`} v={String(s.deals)} />
        <HeroStat k="VOLUME" v={RMk(s.volume)} />
        <HeroStat k="GCI BOOKED" v={RMk(s.gci)} />
      </div>
      <div style={{ fontSize: 11.5, opacity: 0.62, lineHeight: 1.5 }}>
        YTD GCI is recognised when payment is received; the figures below are booked at sale.
      </div>
    </>
  );
};

const HeroStat = ({ k, v }) => (
  <div style={{ flex: 1, minWidth: 0 }}>
    <div style={{ fontSize: 11, letterSpacing: '.06em', opacity: 0.72, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{k}</div>
    <div style={{ fontSize: 16.5, fontWeight: 700, marginTop: 3, whiteSpace: 'nowrap' }}>{v}</div>
  </div>
);

// ── small parts ────────────────────────────────────────────────────────────

const rowSub = (m) => [
  m.gen ? `Gen ${m.gen}` : null,
  RANK_LABEL[m.management_rank] || m.management_rank,
  m.tier ? `${m.tier}% tier` : null,
  m.pipeline_deals ? `${m.pipeline_deals} open` : null,
  m.override ? `override ${RM0(m.override)}` : null,
].filter(Boolean).join(' · ');

const SectionLabel = ({ children }) => (
  <div style={{ fontSize: 11.5, fontWeight: 600, letterSpacing: '.08em', color: 'var(--muted)' }}>{children}</div>
);

const Tile = ({ label, value, sub, color }) => (
  <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: S.radius, padding: S.tilePad, minWidth: 0 }}>
    <div style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 700, letterSpacing: '.05em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</div>
    <div style={{ fontSize: 18, fontWeight: 700, marginTop: 6, color: color || 'var(--text)', whiteSpace: 'nowrap', letterSpacing: '-.01em' }}>{value}</div>
    {sub && <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sub}</div>}
  </div>
);

const Pill = ({ on, onClick, label, small }) => (
  <span
    className="tap"
    onClick={onClick}
    role="button"
    tabIndex={0}
    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
    style={{
      fontSize: small ? 11.5 : 12, fontWeight: 600, padding: small ? '6px 12px' : '7px 13px',
      borderRadius: 999, cursor: 'pointer', whiteSpace: 'nowrap', flex: 'none',
      border: `1px solid ${on ? 'var(--primary)' : 'var(--border)'}`,
      background: on ? 'var(--primary)' : 'var(--surface)',
      color: on ? '#fff' : 'var(--muted)',
    }}
  >
    {label}
  </span>
);
